import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { useTranslate } from '@lumapps/translations';
import { PARAGRAPH } from '@lumapps/wrex-typography/constants';
import { TypographyEditor } from '@lumapps/wrex-typography/types';
import { useSlate, useSlateStatic } from '@lumapps/wrex/slate';
import { focusAt } from '@lumapps/wrex/slate/utils/focusAt';
import { ToolbarItem, WrexEditor } from '@lumapps/wrex/types';

export interface UseTextualBlockToggleButtonConfig<PE extends TypographyEditor> {
    /** Block element type constant (e.g. `CODE_BLOCK`); also used as the data-attributes `action`. */
    type: string;
    /** Toolbar icon (SVG path). */
    icon: string;
    /** Translation key for the button label / tooltip. */
    label: string;
    /** Whether the block can be toggled at the current selection (e.g. `(e) => e.isCodeBlockAllowed()`). */
    isAllowed: (editor: WrexEditor<PE>) => boolean;
    /** Data-attributes scope used to build the button's `data-*` props (e.g. `'structured-content-toolbar'`). */
    scope: string;
    /** Whether the editor is read-only — disables the button. */
    isReadOnly?: boolean;
}

/**
 * Toolbar toggle button shared by textual blocks: toggles the current block between `PARAGRAPH` and the
 * block `type` (via typography's `changeBlockType`). Consolidates `useCodeBlockButton` / `useInfoPanelButton`.
 *
 * @typeParam PE the plugin editor type, combined with `TypographyEditor` (e.g. `CodeBlockEditor & TypographyEditor`).
 */
export const useTextualBlockToggleButton = <PE extends TypographyEditor>({
    type,
    icon,
    label,
    isAllowed,
    scope,
    isReadOnly,
}: UseTextualBlockToggleButtonConfig<PE>) => {
    const editor = useSlate() as WrexEditor<PE>;
    const editorStatic = useSlateStatic() as WrexEditor<PE>;
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(scope);

    const isSelected = editor.getBlockType() === type;
    const isDisabled = Boolean(isReadOnly) || !isAllowed(editor);

    return React.useMemo(
        (): ToolbarItem => ({
            type: 'toggle-option',
            itemKey: type,
            icon,
            label: translateKey(label),
            tooltipLabel: translateKey(label),
            variant: 'icon',
            onClick() {
                const at = editorStatic.selection || editorStatic.getLastSelection();

                if (editorStatic.getBlockType() === type) {
                    editorStatic.changeBlockType(PARAGRAPH, at);
                } else {
                    editorStatic.changeBlockType(type, at);
                }
                focusAt(editorStatic, editorStatic.selection);
            },
            'aria-disabled': isDisabled,
            isChecked: isSelected,
            otherProps: get({ element: 'button', action: type }),
        }),
        [editorStatic, isDisabled, isSelected, get, translateKey, type, icon, label],
    );
};
